<!-- Help Modal Component -->
<template>
  <div
    v-if="isOpen"
    class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
    @click="$emit('close')"
  >
    <div
      class="bg-neutral-900 rounded-lg w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto"
      @click.stop
    >
      <!-- Header -->
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-2xl font-bold text-white">PHOTOS HELP</h2>
        <button
          @click="$emit('close')"
          class="text-gray-400 hover:text-white transition-colors"
        >
          <i class="mdi mdi-close text-xl"></i>
        </button>
      </div>

      <!-- Content -->
      <div class="space-y-6">
        <!-- Page Content -->
        <div class="prose prose-invert max-w-none">
          <component :is="pages[currentPage].content" />
        </div>

        <!-- Navigation -->
        <div
          class="flex justify-between items-center pt-4 border-t border-neutral-800"
        >
          <button
            @click="previousPage"
            class="px-4 py-2 text-white bg-neutral-800 rounded-md hover:bg-neutral-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="currentPage === 0"
          >
            <i class="mdi mdi-arrow-left mr-2"></i>
            Previous
          </button>
          <div class="text-neutral-400">
            Page {{ currentPage + 1 }} of {{ pages.length }}
          </div>
          <button
            v-if="currentPage < pages.length - 1"
            @click="nextPage"
            class="px-4 py-2 text-white bg-[#27bdf4] rounded-md hover:bg-[#1da8dc]"
          >
            Next
            <i class="mdi mdi-arrow-right ml-2"></i>
          </button>
          <button
            v-else
            @click="$emit('close')"
            class="px-4 py-2 text-white bg-[#27bdf4] rounded-md hover:bg-[#1da8dc]"
          >
            Got it!
            <i class="mdi mdi-check ml-2"></i>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  const h = require("vue").h;

  const IntroPage = {
    render(h) {
      return h("div", { class: "space-y-4" }, [
        h(
          "p",
          "This powerful tool helps you manage and visualize your coral growth over time through photos."
        ),

        h("div", { class: "bg-neutral-800 rounded-lg p-4" }, [
          h("h3", { class: "text-lg font-bold mb-2" }, "Key Features:"),
          h("ul", { class: "list-disc list-inside space-y-2" }, [
            h("li", "Organize photos with tags"),
            h("li", "Upload and manage photos with timestamps"),
            h("li", "Interactive timeline playback"),
            h("li", "Create timelapse videos"),
            h("li", "Compare photos side by side"),
            h("li", "Store and access to the source images"),
          ]),
        ]),

        h(
          "p",
          "This guide will walk you through all the essential features to help you get started."
        ),
      ]);
    },
  };

  const TagsPage = {
    render(h) {
      return h("div", { class: "space-y-4" }, [
        h("h3", { class: "text-lg font-bold" }, "Understanding Tags"),

        h(
          "p",
          "Tags help you organize your photos into meaningful categories. Each photo must belong to a tag."
        ),

        h("div", { class: "bg-neutral-800 rounded-lg p-4 space-y-3" }, [
          h("h4", { class: "font-bold" }, "Working with Tags:"),
          h("ul", { class: "list-disc list-inside space-y-2" }, [
            h(
              "li",
              "Select a tag from the dropdown menu to view associated photos"
            ),
            h("li", 'Create a new tag by clicking the "New Tag" button'),
            h(
              "li",
              'Delete a tag and all its photos by clicking the "Delete Tag" button'
            ),
            h(
              "li",
              'Tag names should be descriptive (e.g., "Table A", "Outplant Area B")'
            ),
          ]),
        ]),

        h(
          "div",
          { class: "bg-red-900/20 border border-red-800 rounded-lg p-4 mt-4" },
          [
            h("h4", { class: "font-bold text-red-400" }, "⚠️ Important:"),
            h(
              "p",
              "Deleting a tag will permanently remove all photos associated with it. This action cannot be undone."
            ),
          ]
        ),
      ]);
    },
  };

  const UploadPage = {
    render(h) {
      return h("div", { class: "space-y-4" }, [
        h("h3", { class: "text-lg font-bold" }, "Uploading Photos"),

        h("div", { class: "flex flex-col gap-6" }, [
          h("div", { class: "bg-neutral-800 rounded-lg p-4 space-y-3" }, [
            h("h4", { class: "font-bold" }, "Upload Steps:"),
            h("ol", { class: "list-decimal list-inside space-y-2" }, [
              h("li", 'Click the "Upload Photo" button'),
              h("li", "Select or create a tag for your photo"),
              h("li", "Choose the date when the photo was taken"),
              h("li", "Drag & drop your photo or click to browse"),
              h("li", 'Click "Upload" to start the upload process'),
            ]),
          ]),

          h("div", { class: "bg-neutral-900 rounded-lg overflow-hidden" }, [
            h("video", {
              attrs: {
                src: require("./assets/photos-upload.mp4"),
                controls: true,
                autoplay: false,
                loop: true,
                muted: true,
                class: "w-full h-full object-cover",
              },
            }),
          ]),
        ]),
      ]);
    },
  };

  const PlaybackPage = {
    render(h) {
      return h("div", { class: "space-y-6" }, [
        h(
          "h3",
          { class: "text-lg font-bold" },
          "Timeline and Playback Controls"
        ),

        // Two-column container
        h("div", { class: "grid grid-cols-2 gap-4" }, [
          // Column 1: Playback Controls
          h("div", { class: "bg-neutral-800 rounded-lg p-4 space-y-3" }, [
            h("h4", { class: "font-bold" }, "Playback Controls:"),
            h("ul", { class: "list-disc list-inside space-y-2" }, [
              h("li", "Play/Pause: Start or stop the automatic playback"),
              h("li", "Rewind/Fast Forward: Navigate between photos"),
              h("li", "Loop: Toggle continuous playback"),
              h("li", "Compare: Enable side-by-side comparison mode"),
            ]),
          ]),

          // Column 2: Timeline Features
          h("div", { class: "bg-neutral-800 rounded-lg p-4 space-y-3" }, [
            h("h4", { class: "font-bold" }, "Timeline Features:"),
            h("ul", { class: "list-disc list-inside space-y-2" }, [
              h("li", "Drag the handles to adjust the date range"),
              h("li", "Click on the timeline to jump to that point"),
              h("li", "Time markers show photo distribution"),
            ]),
          ]),
        ]),

        // Video section below
        h("div", { class: "bg-neutral-900 rounded-lg overflow-hidden mt-2" }, [
          h("video", {
            attrs: {
              src: require("./assets/photos-playback.mp4"),
              controls: true,
              autoplay: false,
              loop: true,
              muted: true,
              class: "w-full h-full object-cover",
            },
          }),
        ]),
      ]);
    },
  };

  const DownloadPage = {
    render(h) {
      return h("div", { class: "space-y-6" }, [
        h(
          "h3",
          { class: "text-lg font-bold" },
          "Downloading and Creating Timelapses"
        ),

        // Two-column container
        h("div", { class: "grid grid-cols-2 gap-4" }, [
          // Column 1: Download Options
          h("div", { class: "bg-neutral-800 rounded-lg p-4 space-y-3" }, [
            h("h4", { class: "font-bold" }, "Download Options:"),
            h("ul", { class: "list-disc list-inside space-y-2" }, [
              h(
                "li",
                "Download Current Image: Get the full resolution version of the current photo"
              ),
              h(
                "li",
                "Download as Timelapse: Create and download a video of your selected date range"
              ),
            ]),
          ]),

          // Column 2: Timelapse Settings
          h("div", { class: "bg-neutral-800 rounded-lg p-4 space-y-3" }, [
            h("h4", { class: "font-bold" }, "Timelapse Settings:"),
            h("ul", { class: "list-disc list-inside space-y-2" }, [
              h("li", "Resolution: Choose output video dimensions"),
              h("li", "Frame Rate: Adjust playback speed (12-60 FPS)"),
              h("li", "Quality: Balance between file size and video quality"),
              h("li", "Aspect Ratio Lock: Maintain proper proportions"),
            ]),
          ]),
        ]),

        // Video section
        h("div", { class: "bg-neutral-900 rounded-lg overflow-hidden mt-2" }, [
          h("video", {
            attrs: {
              src: require("./assets/photos-download.mp4"),
              controls: true,
              autoplay: false,
              loop: true,
              muted: true,
              class: "w-full h-full object-cover",
            },
          }),
        ]),
      ]);
    },
  };

  export default {
    name: "HelpModal",
    props: {
      isOpen: {
        type: Boolean,
        required: true,
      },
    },
    data() {
      return {
        currentPage: 0,
        pages: [
          { title: "Welcome to Photos Module", content: IntroPage },
          { title: "Understanding Tags", content: TagsPage },
          { title: "Uploading Photos", content: UploadPage },
          { title: "Timeline and Playback", content: PlaybackPage },
          { title: "Downloads and Timelapses", content: DownloadPage },
        ],
      };
    },
    methods: {
      nextPage() {
        if (this.currentPage < this.pages.length - 1) {
          this.currentPage++;
        }
      },
      previousPage() {
        if (this.currentPage > 0) {
          this.currentPage--;
        }
      },
    },
    watch: {
      isOpen(newVal) {
        if (newVal) {
          this.currentPage = 0;
        }
      },
    },
  };
</script>

<style scoped>
  .prose {
    color: #e5e7eb;
  }

  .prose h3 {
    color: white;
    margin-top: 0;
  }

  .prose h4 {
    color: #e5e7eb;
    margin-top: 0;
  }

  .prose p {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
  }

  .prose ul,
  .prose ol {
    margin-top: 0.5em;
    margin-bottom: 0.5em;
  }

  .prose li {
    margin-top: 0.25em;
    margin-bottom: 0.25em;
  }

  /* Custom Scrollbar Styles */
  .overflow-y-auto {
    scrollbar-width: thin;
    scrollbar-color: oklch(20.5% 0 0) oklch(10.5% 0 0);
  }

  .overflow-y-auto::-webkit-scrollbar {
    width: 4px;
  }

  .overflow-y-auto::-webkit-scrollbar-track {
    background: oklch(10.5% 0 0);
    border-radius: 2px;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb {
    background-color: oklch(20.5% 0 0);
    border-radius: 2px;
    opacity: 0.5;
  }

  .overflow-y-auto::-webkit-scrollbar-thumb:hover {
    background-color: oklch(20.5% 0 0);
  }
</style>
